/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IProgramLanguageDataState } from "../../Interfaces/SiteContent";
import { IGetProgramLanguageFailure, IGetProgramLanguageSuccess } from "./ProgramLanguageAction";
import {
  PROGRAM_LANGUAGE_LOAD_FAILURE,
  PROGRAM_LANGUAGE_LOAD_SUCCESS,
} from "./ProgramLanguageActionTypes";

const initialState: IProgramLanguageDataState = {
  programLanguages: [],
  programLanguageOptionList: [],
  programLanguageLoadError: "",
};

/**
 * Updates state of fetch program language success.
 * @param {IProgramLanguageDataState} state The program language state.
 * @param {IGetProgramLanguageSuccess} action The program language action success.
 * @returns {IProgramLanguageDataState} New program language state.
 */
const applyLoadProgramLanguage = (
  state: IProgramLanguageDataState,
  action: IGetProgramLanguageSuccess
): IProgramLanguageDataState => ({
  ...state,
  programLanguages: action.programLanguages,
  programLanguageOptionList: action.programLanguages.map((x) => ({
    key: x.name,
    text: x.name,
    id: x.id,
  })),
});

/**
 * Updates state of fetch program language failure.
 * @param {IProgramLanguageDataState} state The program language state.
 * @param {IGetProgramLanguageFailure} action The program language action failure.
 * @returns {IProgramLanguageDataState} New program language state.
 */
const applyLoadFailProgramLanguage = (
  state: IProgramLanguageDataState,
  action: IGetProgramLanguageFailure
): IProgramLanguageDataState => ({
  ...state,
  programLanguageLoadError: action.programLanguageLoadError,
});

/**
 * Reducer function for program language.
 * @param {IProgramLanguageDataState} state The program language state.
 * @param {ProgramLanguageActionTypes} action The program language action type.
 * @returns {IProgramLanguageDataState} The program language data state.
 */

function ProgramLanguageReducer(
  state: IProgramLanguageDataState = initialState,
  action
): IProgramLanguageDataState {
  switch (action.type) {
    case PROGRAM_LANGUAGE_LOAD_SUCCESS:
      return applyLoadProgramLanguage(state, action);
    case PROGRAM_LANGUAGE_LOAD_FAILURE:
      return applyLoadFailProgramLanguage(state, action);
    default:
      return state;
  }
}

export default ProgramLanguageReducer;
